import React from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, History, X, Loader2 } from 'lucide-react';

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffSec = Math.max(0, (Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const STATUS_BADGE = {
  completed: 'bg-green-500/20 text-green-400',
  answered: 'bg-green-500/20 text-green-400',
  active: 'bg-green-500/20 text-green-400',
  no_answer: 'bg-yellow-500/20 text-yellow-400',
  busy: 'bg-yellow-500/20 text-yellow-400',
  missed: 'bg-red-500/20 text-red-400',
  failed: 'bg-red-500/20 text-red-400',
};

/**
 * Lightweight call history popover. Renders inside the dialer card and lists
 * the last N (default 10) calls with a one-click redial button per row.
 *
 * Props:
 *   - calls: [{ id, to_number, from_number, direction, status, duration_seconds, started_at }]
 *   - loading: bool
 *   - error: string | ''
 *   - onRedial: (e164OrRaw) => void
 *   - onClose: () => void
 */
export const CallHistoryPopover = ({ calls, loading, error, onRedial, onClose }) => (
  <div
    className="absolute inset-0 z-10 bg-[#0d1117] flex flex-col"
    data-testid="call-history-popover"
  >
    <div className="flex items-center justify-between p-3 border-b border-gray-800">
      <div className="flex items-center gap-2">
        <History className="w-4 h-4 text-teal-400" />
        <span className="text-white text-sm font-semibold">Recent Calls</span>
      </div>
      <button
        onClick={onClose}
        className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
        data-testid="call-history-close-btn"
        aria-label="Close call history"
      >
        <X className="w-4 h-4 text-gray-400" />
      </button>
    </div>

    <div className="flex-1 overflow-y-auto p-2 max-h-80">
      {loading && (
        <div className="flex items-center justify-center py-8" data-testid="call-history-loading">
          <Loader2 className="w-5 h-5 text-teal-400 animate-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="p-3 text-red-400 text-sm text-center" data-testid="call-history-error">
          {error}
        </div>
      )}

      {!loading && !error && calls.length === 0 && (
        <div className="p-6 text-gray-500 text-sm text-center" data-testid="call-history-empty">
          No recent calls yet
        </div>
      )}

      {!loading && !error && calls.map((call) => {
        const isOutbound = call.direction === 'outbound';
        const number = isOutbound ? call.to_number : call.from_number;
        const badge = STATUS_BADGE[call.status] || 'bg-gray-500/20 text-gray-400';
        return (
          <button
            key={call.id}
            onClick={() => onRedial(number)}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#1a1a1f] rounded-lg transition-colors text-left group"
            data-testid={`call-history-row-${call.id}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isOutbound ? 'bg-teal-400/15 text-teal-400' : 'bg-blue-400/15 text-blue-400'
            }`}>
              {isOutbound ? <PhoneOutgoing className="w-4 h-4" /> : <PhoneIncoming className="w-4 h-4" />}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-white text-sm font-mono truncate">{number || 'Unknown'}</p>
                {call.status && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${badge}`}>
                    {call.status.replace('_', ' ')}
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-xs">
                {formatRelative(call.started_at || call.created_at)} · {formatDuration(call.duration_seconds)}
              </p>
            </div>

            <div className="w-8 h-8 rounded-full bg-green-500/0 group-hover:bg-green-500 flex items-center justify-center flex-shrink-0 transition-colors">
              <Phone className="w-4 h-4 text-green-400 group-hover:text-white" />
            </div>
          </button>
        );
      })}
    </div>
  </div>
);
